import { formatDate as coreFormatDate, formatDateTime as coreFormatDateTime } from '@/composables/useFormat';

/**
 * Formatea una fecha según el formato configurado por la compañía.
 *
 * Las ramas de string manejan formatos de ALMACENAMIENTO conocidos (ISO, corto de
 * BigQuery, DD/MM/YYYY de campos custom). Cada una se parsea en su formato de
 * origen y se re-formatea según la configuración: antes se devolvían literales
 * DD/MM/YYYY, lo que ignoraba la configuración en los inputs más comunes.
 */
export const formatDate = (
  date: string | Date | null | { seconds: number; nanoseconds: number } | Array<any>
): string => {
  if (!date) return '-';

  // Si es un array, usar la función de formato de rango
  if (Array.isArray(date)) {
    return formatDateRange(date);
  }

  try {
    if (typeof date === 'string') {
      // Formato YYYY-MM-DD: el core lo parsea textualmente (sin corrimiento de zona)
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return coreFormatDate(date);
      }

      // Formato M/D/YY o MM/DD/YY (formato corto de BigQuery)
      const shortMatch = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
      if (shortMatch) {
        const [, month, day, shortYear] = shortMatch;
        const fullYear = 2000 + parseInt(shortYear, 10);
        return coreFormatDate(
          `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        );
      }

      // Formato DD/MM/YYYY (campos custom almacenados así en Firebase)
      const fullSlashMatch = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (fullSlashMatch) {
        const [, d, m, y] = fullSlashMatch;
        return coreFormatDate(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
      }
    }

    // Date, timestamp de Firestore y otros strings: el core normaliza.
    // El ajuste manual de getTimezoneOffset() que había acá era equivalente a
    // formatear en UTC, que es justo lo que hace el core con sus getters getUTC*.
    return coreFormatDate(date as any, { fallback: '-' });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha inválida';
  }
};

/**
 * Formatea un rango de fechas (array con fecha inicio y fin) a un string
 * @param dateRange - Array con fecha de inicio y fecha de fin
 * @returns String formateado con el rango de fechas (ej: "10-06-2025 al 25-06-2025")
 */
export const formatDateRange = (dateRange: Array<any>): string => {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length < 2) {
    return '-';
  }

  try {
    // Verificamos el formato de cada fecha en el array
    const startDateValue = dateRange[0];
    const endDateValue = dateRange[1];
    
    // formatDate ya parsea cada formato de almacenamiento en su origen
    // (ISO, M/D/YY de BigQuery, DD/MM/YYYY de campos custom, Date, timestamp).
    // Envolver strings en new Date() los reinterpretaba en orden US:
    // "10/06/2025" se leía como 6 de octubre y "25/06/2025" daba Invalid Date.
    const startDate = formatDate(startDateValue);
    const endDate = formatDate(endDateValue);
    
    // Devolver el rango formateado
    return `${startDate} - ${endDate}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Rango inválido';
  }
};

export const formatDateEvents = (date: string | null): string => {
  if (!date) return '-';

  try {
    // Instante en UTC: "2026-08-27T12:12:22.000Z" (así llegan los eventos de
    // DHL Express). Al ser un instante absoluto se muestra en la zona del
    // usuario, a diferencia de las ramas con offset explícito, donde la hora
    // local del lugar del evento es el dato útil y se preserva literal.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(date)) {
      return coreFormatDateTime(date, { seconds: true, fallback: 'Formato inválido' });
    }

    // Primero intentamos con formato que incluye zona horaria
    // Formato: "2025-02-25T00:00:00+01:00"
    const regexWithTZ = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?([+-])(\d{2}):?(\d{2})?/;
    const matchWithTZ = date.match(regexWithTZ);
    
    // Si encontramos formato con zona horaria
    if (matchWithTZ) {
      // Extraer componentes de la fecha con zona horaria
      const [
        _, // Coincidencia completa
        year,
        month,
        day,
        hours,
        minutes,
        seconds,
        sign,
        offsetHours,
        offsetMinutes = '00' // Valor por defecto si no viene especificado
      ] = matchWithTZ;
      
      // La parte de fecha sigue la configuración; la hora y el offset se
      // preservan literales del origen (no se convierte de zona a propósito).
      const datePart = coreFormatDate(`${year}-${month}-${day}`);
      return `${datePart} ${hours}:${minutes}:${seconds} UTC ${sign}${offsetHours}:${offsetMinutes || '00'}`;
    }
    
    // Si no tiene zona horaria, probamos con este formato: "2025-02-25T00:00:00"
    const regexWithoutTZ = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/;
    const matchWithoutTZ = date.match(regexWithoutTZ);
    
    if (matchWithoutTZ) {
      // Extraer componentes de la fecha sin zona horaria
      const [
        _, // Coincidencia completa
        year,
        month,
        day,
        hours,
        minutes,
        seconds
      ] = matchWithoutTZ;
      
      // Sin zona horaria: fecha según configuración + hora literal
      return `${coreFormatDate(`${year}-${month}-${day}`)} ${hours}:${minutes}:${seconds}`;
    }
    
    // Si no coincide con ninguno de los formatos esperados
    console.error('Formato de fecha no reconocido:', date);
    return 'Formato inválido';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha inválida';
  }
};
export const formatDateToISO = (date: Date | string ): string => {
  if(typeof date === "string"){
    const dates = new Date(date);
    const year = dates.getFullYear();
    const month = String(dates.getMonth() + 1).padStart(2, '0');
    const day = String(dates.getDate()).padStart(2, '0');
    const hours = String(dates.getHours()).padStart(2, '0');
    const minutes = String(dates.getMinutes()).padStart(2, '0');
    const seconds = String(dates.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Formatea una fecha con hora según el formato configurado por la compañía.
 * La hora se muestra en la zona local (un timestamp es un instante).
 * @param date - Fecha en formato string ISO o Date
 * @returns String formateado (ej: "28/10/2025 12:00")
 */
export const formatDateTimeDisplay = (
  date: string | Date | null | { seconds: number; nanoseconds: number }
): string => {
  if (!date) return '-';

  try {
    return coreFormatDateTime(date as any, { fallback: '-' });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '-';
  }
};